import { type ChangeEvent, type FormEvent, useEffect, useRef, useState, } from 'react';
import AppButton from '../../../../components/atoms/AppButton/AppButton';
import ImageCropperPostman from '../../../../components/molecules/ImageCropperPostman/ImageCropperPostman';
import AppSelect from '../../../../components/atoms/AppSelect/AppSelect';
import AppInput from '../../../../components/atoms/AppInput/AppInput';
import { Eye, EyeOff } from 'lucide-react';
import { useWarehouse } from '../../hooks/useSelectFields';
import { useRoles } from '../../hooks/useRole';
import { Snackbar } from '../../../../components/atoms/Snackbar/Snackbar';
import { translateRole } from '../../../../utils/roleTranslation';
import { isAdminRole, Role } from '../../../domain/IRoleRepository';
import { UserValidator } from '../../../domain/UserValidator';

interface SnackbarHandle {
    showModal: (value: boolean) => void
}

interface FormData {
    fullName: string
    email: string
    warehouse: string
    password: string
    confirm_password: string
    role_id: number
}

export interface User {
    id: string
    name: string
    email: string
    avatar?: string
    roleId?: number
    warehouseId?: string
}

interface UserFormProps {
    onSubmit?: (data: {
        formData: FormData
        role: string
        roleId: string
        template: number
        file: File | null
    }) => void
    user?: User | null
    mode?: 'create' | 'edit'
}

export default function UserForm({
    onSubmit,
    user = null,
    mode = 'create',
}: UserFormProps) {
    const snackbarRef = useRef<SnackbarHandle>(null)
    const { options: warehouseOptions, loading: warehouseLoading } =
        useWarehouse()
    const { roles, loading: rolesLoading } = useRoles()
    const [roleSelected, setRoleSelected] = useState<Role>()
    const [showPassword, setShowPassword] = useState(false)
    const [formData, setFormData] = useState<FormData>({
        fullName:
            user !== null &&
            user !== undefined &&
            user.name !== null &&
            user.name !== undefined
                ? user.name
                : '',
        email:
            user !== null &&
            user !== undefined &&
            user.email !== null &&
            user.email !== undefined
                ? user.email
                : '',
        warehouse:
            user !== null &&
            user !== undefined &&
            user.warehouseId !== null &&
            user.warehouseId !== undefined
                ? user.warehouseId
                : warehouseOptions[0]?.value,
        password: '',
        confirm_password: '',
        role_id: 0,
    })

    useEffect(() => {
        if (
            user !== null &&
            user !== undefined &&
            user.roleId !== null &&
            user.roleId !== undefined &&
            Number(user.roleId) > 0
        ) {
            const role = roles.find((role) => role.id === Number(user.roleId))
            setRoleSelected(role)
        }
    }, [user?.roleId, roles])

    useEffect(() => {
        if (
            user !== null &&
            user !== undefined &&
            user.warehouseId !== null &&
            user.warehouseId !== undefined &&
            mode === 'edit'
        ) {
            setFormData((prev) => ({
                ...prev,
                warehouse: user.warehouseId ?? prev.warehouse,
            }))
        }
    }, [user?.warehouseId, mode])
    const [file] = useState<File | null>(null)
    const [previewImage] = useState<string | undefined>(
        user !== null &&
            user !== undefined &&
            user.avatar !== null &&
            user.avatar !== undefined
            ? user.avatar
            : undefined
    )

    const handleFullNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, fullName: e.target.value })
    }

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, email: e.target.value })
    }

    const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, password: e.target.value })
    }

    const handleconfirm_passwordChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, confirm_password: e.target.value })
    }

    const isFormValid = UserValidator.validate(formData, roleSelected, mode)

    const passwordsDoNotMatch =
        formData.password.trim() !== '' &&
        formData.confirm_password.trim() !== '' &&
        formData.password !== formData.confirm_password

    const handleWarehouseChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setFormData({ ...formData, warehouse: e.target.value })
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (!isFormValid) return
        try {
            if (onSubmit) {
                onSubmit({
                    formData,
                    role: String(roleSelected?.id),
                    roleId: String(roleSelected?.id),
                    template: 0,
                    file,
                })
            }
        } catch (err) {
            // const errorMessage =
            //     err instanceof Error ? err.message : 'Error desconocido'
            snackbarRef.current?.showModal(true)
        }
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="rounded-lg bg-white p-8 shadow-sm"
        >
            {/* Header */}
            <div className="mb-8 flex items-center">
                <div className="relative">
                    <ImageCropperPostman
                        title={''}
                        description={''}
                        initialImage={previewImage}
                        fullName={formData.fullName}
                    />
                </div>
                <div>
                    <h1 className="text-2xl font-semibold">
                        {mode === 'edit' ? user?.name : 'Crear usuario'}
                    </h1>
                </div>
            </div>

            {/* Role Selection */}
            <div className="mb-6">
                <h2 className="mb-4 text-lg font-bold text-gray-900">Rol</h2>
                {rolesLoading ? (
                    <p className="text-gray-600">Cargando roles...</p>
                ) : (
                    <div className="flex gap-[34px]">
                        {Array.isArray(roles) &&
                            roles.map((roleItem) => (
                                <label
                                    key={roleItem.id}
                                    className="flex cursor-pointer items-center gap-2"
                                >
                                    <input
                                        type="radio"
                                        name="role"
                                        value={String(roleItem.id)}
                                        checked={roleSelected?.id === roleItem.id}
                                        onChange={() =>
                                            setRoleSelected(roleItem)
                                        }
                                        className="size-5 border-2 border-gray-300 accent-[#5a7c3e] focus:ring-2 focus:ring-[#5a7c3e] focus:ring-offset-2"
                                    />
                                    <span className="text-gray-900">
                                        {translateRole(roleItem.name)}
                                    </span>
                                </label>
                            ))}
                    </div>
                )}
            </div>

            {/* General Information */}
            <div className="mb-6">
                <h2 className="mb-6 text-lg font-bold text-gray-900">
                    Información general
                </h2>

                <div className="grid gap-[20px] md:grid-cols-3 items-baseline">
                    {/* Full Name */}
                    <div className="space-y-2">
                        <AppInput
                            id="fullName"
                            type="text"
                            value={formData.fullName}
                            onChange={handleFullNameChange}
                            className="rounded-lg border-gray-300"
                            label="Nombre completo"
                        />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                        <AppInput
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={handleEmailChange}
                            className="rounded-lg border-gray-300"
                            label="Email"
                            disabled={mode === 'edit' ? true : false}
                        />
                    </div>

                    {/* Warehouse */}
                    {roleSelected && !isAdminRole(roleSelected.name) && (
                        <div className="space-y-2">
                            <AppSelect
                                disabled={warehouseLoading}
                                label={'Warehouse'}
                                onChange={handleWarehouseChange}
                                value={formData.warehouse}
                                placeholder={
                                    warehouseLoading
                                        ? 'Cargando warehouse...'
                                        : 'Selecciona un warehouse'
                                }
                                options={warehouseOptions}
                                className="mt-[1px] text-base"
                            />
                        </div>
                    )}
                </div>

                {/* Password Fields */}
                <div className="mt-6 w-[66%]">
                    <div className="grid gap-6 sm:grid-cols-2">
                        {/* Password */}
                        <div className="space-y-2">
                            <div className="relative">
                                <AppInput
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handlePasswordChange}
                                    className="rounded-lg border-gray-300 pr-10"
                                    label="Contraseña"
                                    placeholder={
                                        mode === 'edit'
                                            ? 'Dejar vacío para no cambiar'
                                            : ''
                                    }
                                    addonRight={
                                        showPassword ? (
                                            <EyeOff className="h-5 w-5 text-gray-600" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-600" />
                                        )
                                    }
                                    onIconClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                />
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <div className="relative">
                                <AppInput
                                    id="confirm_password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.confirm_password}
                                    onChange={handleconfirm_passwordChange}
                                    className="rounded-lg border-gray-300 pr-10"
                                    label="Confirmar contraseña"
                                    placeholder={
                                        mode === 'edit'
                                            ? 'Dejar vacío para no cambiar'
                                            : ''
                                    }
                                    addonRight={
                                        showPassword ? (
                                            <EyeOff className="h-5 w-5 text-gray-600" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-600" />
                                        )
                                    }
                                    onIconClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {passwordsDoNotMatch && (
                        <p className="mt-2 text-sm text-red-500">
                            Las contraseñas no coinciden
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-8">
                <AppButton
                    type="submit"
                    disabled={!isFormValid}
                    className="h-12 rounded-lg bg-[#5a7c3e] px-8 text-white hover:bg-[#4a6b2e] disabled:bg-gray-200 disabled:text-gray-400"
                    title={
                        mode === 'edit' ? 'Guardar cambios' : 'Crear usuario'
                    }
                />
            </div>

            <Snackbar
                ref={snackbarRef}
                title="Error"
                description="Ocurrió un error al crear el usuario. Por favor, intenta de nuevo."
                state="errorV2"
                closeAction={true}
                time={5000}
            />
        </form>
    )
}
